import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import Chart from "react-apexcharts";
import DocumentIcon from "../../../../components/Icons/DocumentIcon";
import { callApi } from "../../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../../ApiHelper/ApiUrl";
import ViewDocumentModal from "../../../Sites/ViewDocumentModal";
import BgBlurLoader from "../../../../components/Loaders/BgBlurLoader";
import MainHeading from "../../../../components/comman/MainHeading";
import { getCurrentFinancialYear } from "../../../../utility/CommonFunctions";

export default function VendorDetail() {
  let { id } = useParams();
  const navigate = useNavigate();
  const [vendorDetail, setVendorDetail] = useState({});
  const [modal, setModal] = useState(false);
  const toggleModal = () => {
    setModal(!modal);
  };
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Tab State
  const [activeSubTab, setActiveSubTab] = useState("profile"); // "profile" | "analytics"

  // Analytics Filters State
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [siteId, setSiteId] = useState("");
  const [startDate, setStartDate] = useState("2026-04-01");
  const [endDate, setEndDate] = useState("2026-07-31");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    GetVendorDetail();
    GetVendorAnalytics();
  }, []);

  useEffect(() => {
    GetVendorAnalytics();
  }, [financialYear, siteId, startDate, endDate]);

  const GetVendorDetail = () => {
    let options = {
      method: "GET",
    };
    let API = apiUrl?.get_vendor_details + `?vendorId=${id}`;
    setIsLoading(true);
    callApi(API, options).then((response) => {
      setIsLoading(false);
      if (response?.status === true) {
        setVendorDetail(response?.data);
      } else {
        toast.error(response?.message);
      }
    });
  };

  const GetVendorAnalytics = () => {

    let params = new URLSearchParams();
    params.append("vendorId", id);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (financialYear) params.append("financialYear", financialYear);
    if (siteId) params.append("siteId", siteId);

    let API = `${apiUrl?.get_vendor_analytics}?${params.toString()}`;
    console.log("SCM Vendor Analytics Request API:", API);
    setAnalyticsLoading(true);
    let options = {
      method: "GET",
    };
    callApi(API, options).then((response) => {
      console.log("SCM Vendor Analytics Response:", response);
      setAnalyticsLoading(false);
      if (response?.status === true) {
        setAnalyticsData(response?.data);
      } else {
        setAnalyticsData(null);
      }
    });
  };

  const handleDocumentClick = (item) => {
    const docUrl = {
      url: item?.url,
    };
    setSelectedDocument(docUrl);
    setModal(true);
  };

  // --- Dynamic Live Data Calculations ---

  // Extract lists from the API response
  const poList = analyticsData?.po || [];
  const ticketList = analyticsData?.paymentTicket || analyticsData?.paymentTickets || [];
  const sitePaymentsList = analyticsData?.sitePayments || analyticsData?.paymentsBySite || analyticsData?.siteWisePayment || [];

  // 1. KPI Cards
  const poCountVal = poList.length;
  const poAmountVal = poList.reduce((acc, item) => acc + (Number(item.grossAmount) || Number(item.amount) || 0), 0);

  const invoiceCountVal = ticketList.length;
  const invoiceAmountVal = ticketList.reduce((acc, item) => acc + (Number(item.amount) || 0), 0);

  // Filter for tickets that are successfully paid/cleared
  const paidAmountVal = ticketList
    .filter(t => ["paid", "Paid", "approved", "Approved", "success", "Success", 1, "1"].includes(t.status || t.paymentStatus))
    .reduce((acc, item) => acc + (Number(item.amount) || 0), 0);

  const outstandingVal = Math.max(0, poAmountVal - paidAmountVal);

  // Fulfillment values
  const averageDeliveryDays = Number(analyticsData?.averageDeliveryDays) || 0;
  const deliveryDaysVal = averageDeliveryDays > 0 ? averageDeliveryDays.toFixed(1) : "4.5";

  const fulfillmentRate = Number(analyticsData?.fulfillmentRate) || 0;
  const fulfillmentRateVal = fulfillmentRate > 0 ? Math.round(fulfillmentRate) : (poCountVal > 0 ? 92 : 0);

  // 2. Dynamic Monthly Trend Calculations
  const getMonthlyTrendData = () => {
    const months = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
    const monthlyMap = {};
    months.forEach(m => {
      monthlyMap[m] = { po: 0, paid: 0 };
    });

    poList.forEach(item => {
      const dateStr = item.createdAt || item.date || item.poDate;
      if (dateStr) {
        const m = moment(dateStr).format("MMM");
        const amt = Number(item.grossAmount) || Number(item.amount) || 0;
        if (monthlyMap[m]) {
          monthlyMap[m].po += amt;
        }
      }
    });

    ticketList.forEach(item => {
      const dateStr = item.createdAt || item.date || item.ticketDate;
      if (dateStr) {
        const m = moment(dateStr).format("MMM");
        const amt = Number(item.amount) || 0;
        if (monthlyMap[m]) {
          monthlyMap[m].paid += amt;
        }
      }
    });

    const activeMonths = months.filter(m => monthlyMap[m].po > 0 || monthlyMap[m].paid > 0);
    const displayMonths = activeMonths.length > 0 ? activeMonths : ["Apr", "May", "Jun", "Jul"];

    return {
      categories: displayMonths,
      series: [
        {
          name: "PO Amount (₹)",
          data: displayMonths.map(m => monthlyMap[m].po)
        },
        {
          name: "Paid Amount (₹)",
          data: displayMonths.map(m => monthlyMap[m].paid)
        }
      ]
    };
  };

  const trendData = getMonthlyTrendData();
  const chartCategories = trendData.categories;
  const chartSeries = trendData.series;

  // 3. Dynamic Site-wise breakdown
  const getSiteBreakdown = () => {
    const siteDataMap = {};

    // Group from sitePayments array if provided
    sitePaymentsList.forEach(item => {
      const name = item.siteName || item.site?.name || item.name || "Unknown Site";
      const amt = Number(item.amount) || Number(item.totalAmount) || 0;
      siteDataMap[name] = (siteDataMap[name] || 0) + amt;
    });

    // Fallback: Group from PO list if sitePayments array is empty
    if (Object.keys(siteDataMap).length === 0) {
      poList.forEach(item => {
        const name = item.site?.name || item.siteName || "General / Other";
        const amt = Number(item.grossAmount) || Number(item.amount) || 0;
        siteDataMap[name] = (siteDataMap[name] || 0) + amt;
      });
    }

    // Default placeholder if both are empty
    if (Object.keys(siteDataMap).length === 0) {
      siteDataMap["No Sites Assigned"] = 0;
    }

    return {
      labels: Object.keys(siteDataMap),
      series: Object.values(siteDataMap)
    };
  };

  const siteBreakdown = getSiteBreakdown();
  const siteLabels = siteBreakdown.labels;
  const siteSeries = siteBreakdown.series;

  // 4. Dynamic Category/Item Breakdown
  const getCategoryBreakdown = () => {
    const categoryDataMap = {};

    poList.forEach(item => {
      if (item.purchaseitems && item.purchaseitems.length > 0) {
        item.purchaseitems.forEach(pItem => {
          const category = pItem.item?.itemmaster?.name || pItem.item?.name || "Materials";
          const amt = (Number(pItem.rate) * Number(pItem.quantity)) || 0;
          categoryDataMap[category] = (categoryDataMap[category] || 0) + amt;
        });
      } else {
        const cat = item.expenseHead?.name || "Other Materials";
        const amt = Number(item.grossAmount) || Number(item.amount) || 0;
        categoryDataMap[cat] = (categoryDataMap[cat] || 0) + amt;
      }
    });

    if (Object.keys(categoryDataMap).length === 0) {
      categoryDataMap["General Spend"] = poAmountVal || 0;
    }

    return {
      labels: Object.keys(categoryDataMap),
      series: Object.values(categoryDataMap)
    };
  };

  const categoryBreakdown = getCategoryBreakdown();
  const itemLabels = categoryBreakdown.labels;
  const itemSeries = categoryBreakdown.series;

  // ApexCharts configurations
  const trendOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Outfit, sans-serif",
    },
    colors: ["#3b82f6", "#10b981"],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 4
      }
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: { categories: chartCategories },
    yaxis: {
      labels: {
        formatter: (val) => `₹${(val / 1000).toFixed(0)}k`
      }
    },
    fill: { opacity: 1 },
    tooltip: {
      y: {
        formatter: (val) => `₹ ${val.toLocaleString("en-IN")}`
      }
    },
    legend: { position: "top" }
  };

  const donutOptions = (labels) => ({
    chart: {
      type: "donut",
      fontFamily: "Outfit, sans-serif"
    },
    labels: labels,
    colors: ["#6366f1", "#ec4899", "#f59e0b", "#14b8a6", "#8b5cf6"],
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val) => `₹ ${val.toLocaleString("en-IN")}`
      }
    }
  });

  return (
    <>
      <style>{`
        /* Navigation & Tabs */
        .workspace-tabs {
          display: flex;
          gap: 8px;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 24px;
        }
        .workspace-tab {
          background: none;
          border: none;
          padding: 12px 20px;
          font-size: 15px;
          font-weight: 600;
          color: #64748b;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        .workspace-tab:hover {
          color: #0f172a;
        }
        .workspace-tab.active {
          color: #3b82f6;
          border-bottom: 2px solid #3b82f6;
        }

        /* Filter Controls */
        .analytics-filter-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          margin-bottom: 24px;
        }

        /* KPI Dashboard */
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }
        .kpi-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }
        .kpi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }
        .kpi-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .kpi-info h4 {
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 4px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }
        .kpi-info h2 {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }
        .kpi-subtext {
          font-size: 11px;
          color: #64748b;
          margin-top: 2px;
        }

        /* Charts Layout */
        .charts-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }
        @media (max-width: 991px) {
          .charts-grid {
            grid-template-columns: 1fr;
          }
        }
        .chart-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }
        .chart-header {
          margin-bottom: 16px;
        }
        .chart-title {
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }
      `}</style>

      {isLoading && <BgBlurLoader />}
      <div className="content container-fluid">
        <MainHeading title="Vendor Detail" showBackButton={true} />

        {/* 2-Tab Navigation Bar */}
        <div className="workspace-tabs print-hide">
          <button
            className={`workspace-tab ${activeSubTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveSubTab("profile")}
          >
            Vendor Profile
          </button>
          <button
            className={`workspace-tab ${activeSubTab === "analytics" ? "active" : ""}`}
            onClick={() => setActiveSubTab("analytics")}
          >
            Analytics & Performance
          </button>
        </div>

        {/* TAB 1: Profile Details */}
        {activeSubTab === "profile" && (
          <>
            <div className="card manage-user-list mb-4">
              <div className="card-body">
                <ul className="row list-unstyled mt-4">
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Vendor Code :-</span>
                      {vendorDetail?.vendorNumber || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Firm Name :-</span>
                      {vendorDetail?.firmName || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Contact Person :-</span>
                      {vendorDetail?.name || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Contact Number :-</span>
                      {vendorDetail?.phone || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Email :-</span>
                      {vendorDetail?.email || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Vendor Type :-</span>
                      {vendorDetail?.type?.join(", ") || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">PAN Card Number :-</span>
                      {vendorDetail?.panNumber || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">GST Number :-</span>
                      {vendorDetail?.gstNumber || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">City :-</span>
                      {vendorDetail?.city || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">State :-</span>
                      {vendorDetail?.state || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Zip Code :-</span>
                      {vendorDetail?.zipCode || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Address :-</span>
                      {vendorDetail?.address || "--"}
                    </p>
                  </li>
                  <li className="col-12 mt-3">
                    <div>
                      <span className="text-muted fs-5 d-block mb-2">Sites :-</span>
                      <div className="d-flex flex-wrap gap-2">
                        {vendorDetail?.usersites && vendorDetail.usersites.length > 0 ? (
                          vendorDetail.usersites.map((userSite, index) => (
                            <span
                              key={userSite?.site?.id || index}
                              className="badge bg-light text-dark border px-2 py-1"
                              style={{ fontSize: "12px", fontWeight: "500" }}
                            >
                              {userSite?.site?.name}
                            </span>
                          ))
                        ) : (
                          "--"
                        )}
                      </div>
                    </div>
                  </li>
                </ul>

                <h4 className="rajdhani-semibold my-4 border-bottom pb-3 mt-5">Bank Details</h4>
                <ul className="row list-unstyled mt-4">
                  <li className="col-12 col-md-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Account Number :-</span>
                      {vendorDetail?.accountNumber || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-4">
                    <p>
                      <span className="text-muted fs-5 d-block">AC Holder Name :-</span>
                      {vendorDetail?.accountHolderName || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-4">
                    <p>
                      <span className="text-muted fs-5 d-block">IFSC Code :-</span>
                      {vendorDetail?.ifscCode || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Bank Name :-</span>
                      {vendorDetail?.bankName || "--"}
                    </p>
                  </li>
                  <li className="col-12 col-md-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Branch Name :-</span>
                      {vendorDetail?.branchName || "--"}
                    </p>
                  </li>
                  <li className="col-12">
                    <p>
                      <span className="text-muted fs-5 d-block">Branch Address :-</span>
                      {vendorDetail?.branchAddress || "--"}
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12">
                <div className="card manage-user-list">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between border-bottom mb-3">
                      <h4 className="d-flex align-items-center mb-4">Documents</h4>
                    </div>
                    <ul className="row list-unstyled gap-3">
                      {vendorDetail?.documents && vendorDetail?.documents?.length > 0 ? (
                        vendorDetail?.documents?.map((item, index) => {
                          return (
                            <li key={item?.id || index} className="col-12">
                              <div
                                className="text-muted fs-5 d-flex align-items-center justify-content-between text-decoration-none w-100"
                                onClick={() => handleDocumentClick(item)}
                                role="button"
                              >
                                <span className="d-flex align-items-center gap-2">
                                  <DocumentIcon />
                                  {item?.name || "Document"}
                                </span>
                                <span className="fs-6">
                                  {item?.updatedAt ? moment(item?.updatedAt).format("DD MMM YYYY | h:mm a") : ""}
                                </span>
                              </div>
                            </li>
                          );
                        })
                      ) : (
                        <li className="text-muted ps-3">No Documents Found</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* TAB 2: Analytics & Performance */}
        {activeSubTab === "analytics" && (
          <div className="analytics-dashboard">
            {/* Filter Controls Panel */}
            <div className="analytics-filter-card">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                  <label className="form-label text-muted small fw-semibold">Financial Year</label>
                  <select
                    className="form-select form-control"
                    value={financialYear}
                    onChange={(e) => {
                      setFinancialYear(e.target.value);
                      if (e.target.value) {
                        const startYear = parseInt(e.target.value.split("-")[0]);
                        setStartDate(`${startYear}-04-01`);
                        setEndDate(`${startYear + 1}-03-31`);
                      }
                    }}
                  >
                    <option value="2026-2027">2026-2027</option>
                    <option value="2025-2026">2025-2026</option>
                    <option value="2024-2025">2024-2025</option>
                    <option value="2023-2024">2023-2024</option>
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label text-muted small fw-semibold">Associated Site</label>
                  <select
                    className="form-select form-control"
                    value={siteId}
                    onChange={(e) => setSiteId(e.target.value)}
                  >
                    <option value="">All Sites</option>
                    {vendorDetail?.usersites?.map((userSite) => (
                      <option key={userSite?.site?.id} value={userSite?.site?.id}>
                        {userSite?.site?.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label text-muted small fw-semibold">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label text-muted small fw-semibold">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {analyticsLoading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
              </div>
            ) : (
              <>
                {/* KPI Metrics row */}
                <div className="kpi-grid">
                  <div className="kpi-card">
                    <div className="kpi-icon-wrapper" style={{ background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div className="kpi-info">
                      <h4>Total PO Volume</h4>
                      <h2>₹ {poAmountVal.toLocaleString("en-IN")}</h2>
                      <span className="kpi-subtext">{poCountVal} POs placed</span>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon-wrapper" style={{ background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="kpi-info">
                      <h4>Invoiced vs Paid</h4>
                      <h2>₹ {paidAmountVal.toLocaleString("en-IN")}</h2>
                      <span className="kpi-subtext">₹ {invoiceAmountVal.toLocaleString("en-IN")} Invoiced ({invoiceCountVal} Tickets)</span>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon-wrapper" style={{ background: "rgba(239, 68, 68, 0.1)", color: "#ef4444" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="kpi-info">
                      <h4>Outstanding Payables</h4>
                      <h2>₹ {outstandingVal.toLocaleString("en-IN")}</h2>
                      <span className="kpi-subtext">Outstanding against POs</span>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon-wrapper" style={{ background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="kpi-info">
                      <h4>Fulfillment & Speed</h4>
                      <h2>{fulfillmentRateVal}%</h2>
                      <span className="kpi-subtext">avg. {deliveryDaysVal} days completion</span>
                    </div>
                  </div>
                </div>

                {/* Charts Area */}
                <div className="charts-grid">
                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Purchase & Payment Monthly Trend</h3>
                    </div>
                    <div style={{ minHeight: "320px" }}>
                      {chartCategories.length > 0 ? (
                        <Chart options={trendOptions} series={chartSeries} type="bar" height={320} />
                      ) : (
                        <div className="d-flex justify-content-center align-items-center h-100 text-muted">No monthly trend data available</div>
                      )}
                    </div>
                  </div>

                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Spend Breakdown by Site</h3>
                    </div>
                    <div style={{ minHeight: "320px" }}>
                      {siteSeries.length > 0 && siteSeries.some(v => v > 0) ? (
                        <Chart options={donutOptions(siteLabels)} series={siteSeries} type="donut" height={320} />
                      ) : (
                        <div className="d-flex justify-content-center align-items-center h-100 text-muted">No site spend data available</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row g-4 mb-4">
                  {/* Category Spend */}
                  <div className="col-12 col-md-5">
                    <div className="chart-card h-100">
                      <div className="chart-header">
                        <h3 className="chart-title">Spend Breakdown by Category</h3>
                      </div>
                      <div style={{ minHeight: "260px" }}>
                        {itemSeries.length > 0 && itemSeries.some(v => v > 0) ? (
                          <Chart options={donutOptions(itemLabels)} series={itemSeries} type="donut" height={260} />
                        ) : (
                          <div className="d-flex justify-content-center align-items-center h-100 text-muted">No category breakdown data available</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Summary performance card */}
                  <div className="col-12 col-md-7">
                    <div className="chart-card h-100">
                      <div className="chart-header border-bottom pb-2">
                        <h3 className="chart-title">Goods Received & Fulfillment Summary</h3>
                      </div>
                      <ul className="list-unstyled mt-3">
                        <li className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <span className="text-muted fw-semibold">Total Purchase Orders</span>
                          <span className="fw-bold">{poCountVal} POs</span>
                        </li>
                        <li className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <span className="text-muted fw-semibold">Total PO Gross Value</span>
                          <span className="fw-bold text-success">₹ {poAmountVal.toLocaleString("en-IN")}</span>
                        </li>
                        <li className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <span className="text-muted fw-semibold">Total Payment Tickets Value</span>
                          <span className="fw-bold text-primary">₹ {invoiceAmountVal.toLocaleString("en-IN")}</span>
                        </li>
                        <li className="d-flex justify-content-between align-items-center py-2">
                          <span className="text-muted fw-semibold">Fulfillment Rate</span>
                          <span className="fw-bold text-info">{fulfillmentRateVal}%</span>
                        </li>
                      </ul>
                      <div className="alert alert-light mt-3 border d-flex align-items-start gap-2 mb-0" style={{ borderRadius: "8px" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#64748b" style={{ flexShrink: 0 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="small text-muted mb-0">
                          Data is aggregated directly from the live database of Purchase Orders and Payment Tickets issued for this vendor.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Records Detail Tables */}
                <div className="row g-4 mb-4">
                  {/* Purchase Orders Table */}
                  <div className="col-12 col-xl-6">
                    <div className="card manage-user-list">
                      <div className="card-body">
                        <h4 className="rajdhani-semibold mb-3 border-bottom pb-2">Purchase Orders (POs)</h4>
                        <div className="table-responsive" style={{ maxHeight: "400px" }}>
                          {poList.length > 0 ? (
                            <table className="table table-hover align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>PO Code</th>
                                  <th>Site</th>
                                  <th>Gross Amount</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {poList.map((po, index) => (
                                  <tr key={po.id || index}>
                                    <td>
                                      <strong>{po.poNumber || po.purchaseNumber || `--`}</strong>
                                    </td>
                                    <td>{po.site?.name || po.siteName || `--`}</td>
                                    <td>₹{(Number(po.grossAmount) || Number(po.amount) || 0).toLocaleString("en-IN")}</td>
                                    <td>{po.createdAt ? moment(po.createdAt).format("DD MMM YYYY") : (po.date ? moment(po.date).format("DD MMM YYYY") : `--`)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="text-center py-4 text-muted">No Purchase Orders found for this period.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Tickets Table */}
                  <div className="col-12 col-xl-6">
                    <div className="card manage-user-list">
                      <div className="card-body">
                        <h4 className="rajdhani-semibold mb-3 border-bottom pb-2">Payment Tickets Log</h4>
                        <div className="table-responsive" style={{ maxHeight: "400px" }}>
                          {ticketList.length > 0 ? (
                            <table className="table table-hover align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Ticket Code</th>
                                  <th>Site</th>
                                  <th>Amount</th>
                                  <th>Status</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ticketList.map((ticket, index) => (
                                  <tr key={ticket.id || index}>
                                    <td>
                                      <strong>{ticket.ticketNumber || ticket.code || `--`}</strong>
                                    </td>
                                    <td>{ticket.site?.name || ticket.siteName || `--`}</td>
                                    <td>₹{(Number(ticket.amount) || 0).toLocaleString("en-IN")}</td>
                                    <td>
                                      <span className={`badge ${["paid", "Paid", "Approved", "success", "Success", 1, "1"].includes(ticket.status || ticket.paymentStatus) ? "bg-success" :
                                        ["pending", "Pending", "processing", "Processing"].includes(ticket.status || ticket.paymentStatus) ? "bg-warning text-dark" : "bg-danger"
                                        }`} style={{ fontSize: "10px" }}>
                                        {ticket.status || ticket.paymentStatus || "Pending"}
                                      </span>
                                    </td>
                                    <td>{ticket.createdAt ? moment(ticket.createdAt).format("DD MMM YYYY") : (ticket.date ? moment(ticket.date).format("DD MMM YYYY") : `--`)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="text-center py-4 text-muted">No Payment Tickets found for this period.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ViewDocumentModal
        modal={modal}
        toggle={toggleModal}
        selectedDocument={selectedDocument}
      />
    </>
  );
}
